import type { InputHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * Form primitives styled for purple/dark backgrounds (Home ContactSection).
 *
 * The light-theme equivalents live in components/ui/{FormField,Input,Checkbox}.
 * These mirror that API but render with white labels, transparent borders,
 * and yellow accents to read on a dark hero.
 */

interface DarkFieldProps {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
}

export function DarkField({ id, label, required, error, children }: DarkFieldProps) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={id} className="text-xs md:text-lg font-medium text-white">
        {label}
        {required && (
          <span aria-hidden="true" className="text-brand-yellow ml-0.5">
            *
          </span>
        )}
      </label>
      {children}
      {error && (
        <p role="alert" className="text-xs text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}

type DarkInputProps = InputHTMLAttributes<HTMLInputElement> & {
  hasError?: boolean;
};

export function DarkInput({ hasError, className, ...rest }: DarkInputProps) {
  return (
    <input
      className={cn(
        'autofill-purple w-full rounded-full border bg-brand-purple px-5 py-3 text-sm text-white',
        'placeholder:text-white/40 outline-none',
        hasError
          ? 'border-red-400/70'
          : 'border-white/20 hover:border-white/40',
        className,
      )}
      {...rest}
    />
  );
}

interface DarkCheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  hasError?: boolean;
}

export function DarkCheckbox({
  id,
  label,
  hasError,
  className,
  ...rest
}: DarkCheckboxProps) {
  return (
    <label
      htmlFor={id}
      className={cn('flex items-start gap-3 cursor-pointer group', className)}
    >
      <span className="relative mt-0.5 shrink-0">
        <input id={id} type="checkbox" className="peer sr-only" {...rest} />
        <span
          aria-hidden="true"
          className={cn(
            'block h-5 w-5 rounded-md border-2 transition-colors',
            'group-hover:border-white',
            'peer-checked:bg-brand-yellow peer-checked:border-brand-yellow',
            'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-yellow',
            hasError ? 'border-red-400/70' : 'border-white/40',
          )}
        />
        <svg
          aria-hidden="true"
          viewBox="0 0 24 24"
          className="pointer-events-none absolute inset-0 m-auto h-4 w-4 text-brand-purple opacity-0 peer-checked:opacity-100 transition-opacity"
        >
          <path
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 12l5 5L20 7"
          />
        </svg>
      </span>
      <span className="text-sm text-white/90 leading-snug">{label}</span>
    </label>
  );
}
